import {
  GetStepTransitionsByTaskIdDocument,
  GetTaskByIdDocument,
  WorkflowStepCode,
  useGetStepTransitionsByTaskIdQuery,
  useUpdateTaskStatusMutation,
} from '@exam-management/graphql';
import { useNavigate } from '@exam-management/router';
import { isTaskOverdueError, useQSetReviewModal } from '@exam-management/task-management';
import { Button } from '@shared/components';
import debounce from 'lodash/debounce';
import { useLocation } from 'react-router-dom';

interface StartReviewButtonProps {
  mainTaskId: string;
  subTaskId: string;
  subtaskStatus: string;
}

const BUTTON_CONFIG: Record<
  string,
  { label: string; variant: 'contained' | 'outlined'; color: 'warning' | 'primary' } | undefined
> = {
  [WorkflowStepCode.ReviewQsetTodo]: {
    label: 'Start Review',
    variant: 'contained',
    color: 'warning',
  },
  [WorkflowStepCode.ReviewQsetInReview]: {
    label: 'Continue Review',
    variant: 'outlined',
    color: 'primary',
  },
  [WorkflowStepCode.ReviewQsetReviewed]: {
    label: 'View Details',
    variant: 'outlined',
    color: 'primary',
  },
};

export default function StartReviewButton(props: Readonly<StartReviewButtonProps>) {
  const { mainTaskId, subTaskId, subtaskStatus } = props;

  const { setAction } = useQSetReviewModal();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const subtaskRouteTemplate = pathname.startsWith('/my-task')
    ? '/my-task/qset-review/:taskId/subtask/:subTaskId'
    : '/task-management/qset-review/:taskId/subtask/:subTaskId';

  const config = BUTTON_CONFIG[subtaskStatus];

  const isTodo = subtaskStatus === WorkflowStepCode.ReviewQsetTodo;

  const { data } = useGetStepTransitionsByTaskIdQuery({
    variables: {
      getStepTransitionsByTaskIdInput: {
        taskId: subTaskId,
      },
    },
    skip: !isTodo,
  });

  const [update, { loading: isLoading }] = useUpdateTaskStatusMutation({
    variables: {
      updateTaskStatusInput: {
        taskId: subTaskId,
        workflowStepId: data?.wflGetStepTransitionsByTaskId.steps?.[0]?.id ?? '',
      },
    },
    refetchQueries: [
      {
        query: GetStepTransitionsByTaskIdDocument,
        variables: {
          getStepTransitionsByTaskIdInput: {
            taskId: subTaskId,
          },
        },
      },
      {
        query: GetTaskByIdDocument,
        variables: {
          getTaskByIdInput: {
            taskId: mainTaskId,
          },
        },
      },
    ],
    onCompleted: () => {
      navigate(subtaskRouteTemplate, {
        params: {
          taskId: mainTaskId,
          subTaskId,
        },
      });
    },
    onError: (error) => {
      if (isTaskOverdueError(error)) {
        setAction('OVERDUE_NOTICE');
      }
    },
  });

  const debouncedHandleStartReviewClick = debounce(() => {
    update();
  }, 500);

  const handleClick = () => {
    if (isTodo) {
      debouncedHandleStartReviewClick();
    } else {
      navigate(subtaskRouteTemplate, {
        params: {
          taskId: mainTaskId,
          subTaskId,
        },
      });
    }
  };

  if (!config) {
    return null;
  }

  return (
    <Button
      variant={config.variant}
      color={config.color}
      loading={isTodo ? isLoading : undefined}
      onClick={(e) => {
        e.stopPropagation();
        handleClick();
      }}
      sx={(theme) => ({
        py: theme.spacing(1.5),
        px: theme.spacing(3),
        height: 'fit-content',
        whiteSpace: 'nowrap',
      })}
    >
      {config.label}
    </Button>
  );
}
